import { supabase } from '@/lib/supabase'

export type InitSubscriptionCheckoutResult = {
  clientSecret: string
  subscriptionId: string | null
  publishable?: string | null
  noPaymentRequired: boolean
  no_payment_required: boolean
}

export type InitTrialSetupIntentResult = {
  clientSecret: string
  publishable?: string | null
}

export type StartTrialSubscriptionResult = {
  subscriptionId: string | null
  trialEndsAt: string | null
}

export type StartRewardsTrialSubscriptionResult = {
  subscriptionId: string | null
  trialEndsAt: string | null
  rewards?: {
    window_days?: number
    period_days?: number
    min_volume_usdt?: number
    deadline_at?: string
  } | null
}

async function parseInvokeError(
  error: any,
  response: Response | undefined,
  email?: string | null,
): Promise<{ dup?: any; message?: string | null }> {
  const ctx: Response | undefined = error?.context || response
  if (!ctx) return { message: error?.message || null }
  try {
    const ct = (ctx.headers.get('content-type') || '').toLowerCase()
    const isJson = ct.includes('application/json')
    const body = isJson
      ? await ctx.json().catch(() => null)
      : await ctx.text().catch(() => '')
    if (body && typeof body === 'object' && (body as any).error === 'subscription_already_exists') {
      const dup: any = new Error((body as any).message || 'subscription_already_exists')
      dup.code = 'subscription_already_exists'
      dup.subscription = body
      dup.email = (email || '').trim() || null
      return { dup }
    }
    if (typeof body === 'string' && body) return { message: body }
    if (body && typeof body === 'object') {
      const msg = (body as any)?.message || (body as any)?.error
      if (msg) return { message: String(msg) }
    }
  } catch {}
  const rawMessage = String(error?.message || '')
  const lower = rawMessage.toLowerCase()
  const looksDuplicate =
    lower.includes('subscription_already_exists') || lower.includes('assinatura ativa')
  if (looksDuplicate) {
    let parsed: any = null
    const start = rawMessage.indexOf('{')
    const end = rawMessage.lastIndexOf('}')
    if (start >= 0 && end > start) {
      const jsonRaw = rawMessage.slice(start, end + 1)
      try { parsed = JSON.parse(jsonRaw) } catch {}
    }
    const dup: any = new Error(parsed?.message || rawMessage || 'subscription_already_exists')
    dup.code = 'subscription_already_exists'
    dup.subscription = parsed || null
    dup.email = (email || '').trim() || null
    return { dup }
  }
  return { message: error?.message || null }
}

export async function getStripePublishableKey(): Promise<string> {
  // 1) Preferir configuracao por ambiente (Settings -> Stripe), com override do master
  try {
    const { getStripeKeysEnv, getStripeModes } = await import('@/modules/settings/services/settingsService')
    const cfg = await getStripeKeysEnv()
    const modes = await getStripeModes().catch(() => ({} as any))
    const host = (typeof window !== 'undefined' ? (window.location?.hostname || '') : '').toLowerCase()
    const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '::1' || host.endsWith('.local')
    const defaultMode: 'test' | 'live' = isLocal ? 'test' : 'live'
    const override = isLocal ? (modes.mode_local as any) : (modes.mode_site as any)
    const mode: 'test' | 'live' =
      override === 'live' || override === 'test'
        ? override
        : (cfg as any).mode === 'live' || (cfg as any).mode === 'test'
          ? (cfg as any).mode
          : defaultMode
    const pick =
      (mode === 'live' ? cfg.live.publishable : cfg.test.publishable) ||
      (defaultMode === 'live' ? cfg.live.publishable : cfg.test.publishable) ||
      cfg.live.publishable ||
      cfg.test.publishable
    if (pick && pick.trim()) return pick.trim()
  } catch {}

  // 2) Prefer env var (vite) para resiliencia quando RPC ainda nao disponivel
  const envKey = (import.meta as any)?.env?.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined
  if (envKey && envKey.trim().length > 0) return envKey.trim()

  // 3) Tentar RPC publico (migracao get_public_stripe_key)
  try {
    const { data } = await supabase.rpc('get_public_stripe_key')
    const row = Array.isArray(data) ? data[0] : data
    const key = row?.publishable as string | null
    if (key && key.trim().length > 0) return key.trim()
  } catch {}

  // 4) Fallback: tentar RPC admin (se sessao permitir)
  try {
    const { data } = await supabase.rpc('get_stripe_keys')
    const row = Array.isArray(data) ? data[0] : data
    const key = row?.publishable as string | null
    if (key && key.trim().length > 0) return key.trim()
  } catch {}

  throw new Error(
    'Stripe publishable key nao configurada. Defina VITE_STRIPE_PUBLISHABLE_KEY no .env ou aplique a migracao SQL (get_public_stripe_key).',
  )
}

export async function initSubscriptionCheckout(params: {
  plan: string
  interval?: 'month' | 'year'
  referralCode?: string
  couponCode?: string
  email?: string
}): Promise<InitSubscriptionCheckoutResult> {
  // Garante e-mail quando usuario ja esta logado e sinaliza ambiente do cliente
  const payload: any = { ...params }
  try {
    const host = (typeof window !== 'undefined' ? (window.location?.hostname || '') : '').toLowerCase()
    const isLocal =
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host === '::1' ||
      host.endsWith('.local')
    payload.client_env = isLocal ? 'local' : 'site'
    payload.client_host = host
  } catch {}
  try {
    if (!payload.email) {
      const res = await supabase.auth.getUser()
      const u = (res as any)?.data?.user
      if (u?.email) payload.email = u.email
    }
  } catch {}
  // Tenta chamar via invoke
  try {
    const { data, error, /* @ts-ignore */ response } = await (supabase.functions as any).invoke('init-subscription-checkout', { body: payload })
    if (error) {
      const parsed = await parseInvokeError(error, response as any, payload.email)
      if (parsed.dup) throw parsed.dup
      const msg = parsed.message || error.message || 'Falha ao iniciar checkout'
      throw new Error(msg)
    }
    const r = (Array.isArray(data) ? data[0] : data) as any
    if (r?.error === 'subscription_already_exists') {
      const dup: any = new Error(r?.message || 'subscription_already_exists')
      dup.code = 'subscription_already_exists'
      dup.subscription = r
      dup.email = (payload.email || '').trim() || null
      throw dup
    }
    const clientSecret = r?.client_secret || r?.clientSecret || ''
    const noPay = !!r?.no_payment_required
    if (!clientSecret && !noPay) throw new Error('Falha ao iniciar checkout: client_secret ausente')
    return {
      clientSecret,
      subscriptionId: r?.subscription_id || r?.subscriptionId || null,
      publishable: r?.publishable ?? null,
      noPaymentRequired: noPay,
      no_payment_required: noPay,
    }
  } catch (err: any) {
    // Fallback HTTP (apikey + Authorization)
    try {
      const mod = (await import('@/lib/supabase')) as any
      const SUPABASE_URL: string = mod.SUPABASE_URL
      const SUPABASE_ANON_KEY: string = mod.SUPABASE_ANON_KEY
      const url =
        SUPABASE_URL.replace(/\/$/, '') + '/functions/v1/init-subscription-checkout'
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(payload),
      })
      const isJson = (res.headers.get('content-type') || '')
        .toLowerCase()
        .includes('application/json')
      const body = isJson
        ? await res.json().catch(() => null)
        : await res.text().catch(() => '')
      if (!res.ok) {
        if (isJson && body && typeof body === 'object' && (body as any).error === 'subscription_already_exists') {
          const dup: any = new Error((body as any).message || 'subscription_already_exists')
          dup.code = 'subscription_already_exists'
          dup.subscription = body
          dup.email = (payload.email || '').trim() || null
          throw dup
        }
        const msg =
          (isJson
            ? (body as any)?.message || (body as any)?.error || body
            : body) || 'init-subscription-checkout failed'
        throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg))
      }
      const json: any = isJson
        ? (body as any)
        : (() => {
            try {
              return JSON.parse(String(body || '{}'))
            } catch {
              return {}
            }
          })()
      const clientSecret = json?.client_secret || ''
      const noPay = !!json?.no_payment_required
      if (!clientSecret && !noPay) {
        throw new Error('Falha ao iniciar checkout: client_secret ausente (fallback)')
      }
      return {
        clientSecret,
        subscriptionId: json?.subscription_id || null,
        publishable: json?.publishable ?? null,
        noPaymentRequired: noPay,
        no_payment_required: noPay,
      }
    } catch (e: any) {
      if (e?.code === 'subscription_already_exists') throw e
      throw new Error(e?.message || err?.message || 'Failed to send a request to the Edge Function')
    }
  }
}

export async function initTrialSetupIntent(params: {
  email?: string
  referralCode?: string
  name?: string
  phone?: string
}): Promise<InitTrialSetupIntentResult> {
  const payload: any = { ...params }
  try {
    const host = (typeof window !== 'undefined' ? (window.location?.hostname || '') : '').toLowerCase()
    const isLocal =
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host === '::1' ||
      host.endsWith('.local')
    payload.client_env = isLocal ? 'local' : 'site'
  } catch {}
  try {
    if (!payload.email) {
      const res = await supabase.auth.getUser()
      const u = (res as any)?.data?.user
      if (u?.email) payload.email = u.email
    }
  } catch {}

  try {
    const { data, error } = await supabase.functions.invoke('init-trial-setup-intent', { body: payload })
    if (error) throw error
    const r = (Array.isArray(data) ? data[0] : data) as any
    const clientSecret = r?.client_secret || r?.clientSecret || ''
    if (!clientSecret) {
      throw new Error('Falha ao iniciar setup de cartao: client_secret ausente')
    }
    return {
      clientSecret,
      publishable: r?.publishable ?? null,
    }
  } catch (err: any) {
    try {
      const mod = (await import('@/lib/supabase')) as any
      const SUPABASE_URL: string = mod.SUPABASE_URL
      const SUPABASE_ANON_KEY: string = mod.SUPABASE_ANON_KEY
      const url =
        SUPABASE_URL.replace(/\/$/, '') + '/functions/v1/init-trial-setup-intent'
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(payload),
      })
      const isJson = (res.headers.get('content-type') || '')
        .toLowerCase()
        .includes('application/json')
      const body = isJson
        ? await res.json().catch(() => null)
        : await res.text().catch(() => '')
      if (!res.ok) {
        const msg =
          (isJson
            ? (body as any)?.message || (body as any)?.error || body
            : body) || 'init-trial-setup-intent failed'
        throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg))
      }
      const json: any = isJson
        ? (body as any)
        : (() => {
            try {
              return JSON.parse(String(body || '{}'))
            } catch {
              return {}
            }
          })()
      const clientSecret = json?.client_secret || ''
      if (!clientSecret) {
        throw new Error(
          'Falha ao iniciar setup de cartao: client_secret ausente (fallback)',
        )
      }
      return {
        clientSecret,
        publishable: json?.publishable ?? null,
      }
    } catch (e: any) {
      throw new Error(
        e?.message ||
          err?.message ||
          'Failed to send a request to the init-trial-setup-intent function',
      )
    }
  }
}

export async function startTrialSubscription(params: {
  plan: string
  interval?: 'month' | 'year'
  referralCode?: string
  email?: string
  couponCode?: string
}): Promise<StartTrialSubscriptionResult> {
  const payload: any = { ...params }
  try {
    const host = (typeof window !== 'undefined' ? (window.location?.hostname || '') : '').toLowerCase()
    const isLocal =
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host === '::1' ||
      host.endsWith('.local')
    payload.client_env = isLocal ? 'local' : 'site'
  } catch {}
  try {
    if (!payload.email) {
      const res = await supabase.auth.getUser()
      const u = (res as any)?.data?.user
      if (u?.email) payload.email = u.email
    }
  } catch {}

  try {
    const { data, error } = await supabase.functions.invoke('start-trial-subscription', { body: payload })
    if (error) throw error
    const r = (Array.isArray(data) ? data[0] : data) as any
    return {
      subscriptionId: r?.subscription_id || null,
      trialEndsAt: r?.trial_ends_at || null,
    }
  } catch (err: any) {
    try {
      const mod = (await import('@/lib/supabase')) as any
      const SUPABASE_URL: string = mod.SUPABASE_URL
      const SUPABASE_ANON_KEY: string = mod.SUPABASE_ANON_KEY
      const url =
        SUPABASE_URL.replace(/\/$/, '') +
        '/functions/v1/start-trial-subscription'
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(payload),
      })
      const isJson = (res.headers.get('content-type') || '')
        .toLowerCase()
        .includes('application/json')
      const body = isJson
        ? await res.json().catch(() => null)
        : await res.text().catch(() => '')
      if (!res.ok) {
        const msg =
          (isJson
            ? (body as any)?.message || (body as any)?.error || body
            : body) || 'start-trial-subscription failed'
        throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg))
      }
      const json: any = isJson
        ? (body as any)
        : (() => {
            try {
              return JSON.parse(String(body || '{}'))
            } catch {
              return {}
            }
          })()
      return {
        subscriptionId: json?.subscription_id || null,
        trialEndsAt: json?.trial_ends_at || null,
      }
    } catch (e: any) {
      throw new Error(
        e?.message ||
          err?.message ||
          'Failed to send a request to the start-trial-subscription function',
      )
    }
  }
}

export async function startRewardsTrialSubscription(params: {
  plan: string
  interval?: 'month' | 'year'
  referralCode?: string
  email?: string
  couponCode?: string
}): Promise<StartRewardsTrialSubscriptionResult> {
  const payload: any = { ...params }
  try {
    const host = (typeof window !== 'undefined' ? (window.location?.hostname || '') : '').toLowerCase()
    const isLocal =
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host === '::1' ||
      host.endsWith('.local')
    payload.client_env = isLocal ? 'local' : 'site'
  } catch {}
  try {
    if (!payload.email) {
      const res = await supabase.auth.getUser()
      const u = (res as any)?.data?.user
      if (u?.email) payload.email = u.email
    }
  } catch {}

  try {
    const { data, error } = await supabase.functions.invoke('start-rewards-trial-subscription', { body: payload })
    if (error) throw error
    const r = (Array.isArray(data) ? data[0] : data) as any
    if (r?.error) throw new Error(String(r.error))
    return {
      subscriptionId: r?.subscription_id || r?.subscriptionId || null,
      trialEndsAt: r?.trial_ends_at || r?.trialEndsAt || null,
      rewards: r?.rewards || null,
    }
  } catch (err: any) {
    try {
      const mod = (await import('@/lib/supabase')) as any
      const SUPABASE_URL: string = mod.SUPABASE_URL
      const SUPABASE_ANON_KEY: string = mod.SUPABASE_ANON_KEY
      const url = SUPABASE_URL.replace(/\/$/, '') + '/functions/v1/start-rewards-trial-subscription'
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(payload),
      })
      const isJson = (res.headers.get('content-type') || '').toLowerCase().includes('application/json')
      const body = isJson ? await res.json().catch(() => null) : await res.text().catch(() => '')
      if (!res.ok) {
        const msg = (isJson ? (body as any)?.message || (body as any)?.error || body : body) || 'start-rewards-trial-subscription failed'
        throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg))
      }
      const json: any = isJson
        ? (body as any)
        : (() => {
            try {
              return JSON.parse(String(body || '{}'))
            } catch {
              return {}
            }
          })()
      if (json?.error) throw new Error(String(json.error))
      return {
        subscriptionId: json?.subscription_id || null,
        trialEndsAt: json?.trial_ends_at || null,
        rewards: json?.rewards || null,
      }
    } catch (e: any) {
      throw new Error(e?.message || err?.message || 'Failed to send a request to the start-rewards-trial-subscription function')
    }
  }
}
